import axios from "axios";
import tokenStorage from "@/tokenStorage";
import { paramsSerializer } from "@/axiosHelpers";
import store from "@/store";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import OrderLineAPI from "@/features/order-line/OrderLineAPI";
import StaffEventsAPI from "@/features/events/StaffEventsAPI";
import AddressBookAPI from "@/features/address-book/AddressBookAPI";
import CampusAPI from "@/features/campuses/CampusAPI";
import FilterBookmarksAPI from "@/features/filter-bookmarks/FilterBookmarksAPI";
import SystemEventsAPI from "@/dashboard/systemEvents/SystemEventsAPI";
import SMSAPI from "@/features/sms/SMSAPI";
import DictionaryAPI from "@/features/dictionary/DictionaryAPI";
import WebSocketAPI from "@/features/ws/WebSocketAPI";
import InterviewAPI from "@/features/interview/InterviewAPI";
import HelpReferenceAPI from "@/features/help-reference/HelpReferenceAPI";
import SchoolAPI from "@/features/schools/SchoolAPI";
import EmailTemplateTagsAPI from "@/features/email-template-tags/EmailTemplateTagsAPI";

const api = {
  raw: axios,
  tokenTimeoutID: null,
  ws: WebSocketAPI,
  init() {
    this.raw.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    this.raw.defaults.baseURL = "/api/v1/";
    this.raw.defaults.paramsSerializer = paramsSerializer;
    this.raw.defaults.xsrfCookieName = "csrftoken";
    this.raw.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    setTimeout(() => this.ws.initWebSocket(), 100);
  },
  setToken({ access_token, refresh_token }) {
    tokenStorage.set(access_token, refresh_token);
    this.ws.initWebSocket();
  },
  clearToken() {
    tokenStorage.clear();
  },
  isTokenSet() {
    return !!tokenStorage.getAccessToken();
  },
  logout() {
    return this.raw
      .post("/accounts/logout/")
      .then(() => {
        this.clearToken();
        this.ws.close();
        if (store.getters.isEmbed) {
          window.location.href = store.state.embedStartUrl;
        } else {
          window.location.reload();
        }
      })
      .catch(() => null);
  },
  fetchProfileWithDocuments(id, url) {
    const promise = Promise.all([
      this.raw.get(url),
      store.dispatch("documents/fetchProfileDocuments", id),
    ]);
    const cleanup = () => store.commit("documents/setDocuments", {});
    return [promise, cleanup];
  },
  orderLine: OrderLineAPI,
  staffEvents: StaffEventsAPI,
  addressBook: AddressBookAPI,
  campus: CampusAPI,
  filterBookmarks: FilterBookmarksAPI,
  systemEvents: SystemEventsAPI,
  sms: SMSAPI,
  dictionary: DictionaryAPI,
  interview: InterviewAPI,
  helpReference: HelpReferenceAPI,
  school: SchoolAPI,
  emailTemplateTags: EmailTemplateTagsAPI,
};

const errorInterceptor = (error) => {
  if (!error.response) {
    return Promise.reject(error);
  }
  if (401 === error.response.status) {
    // eslint-disable-next-line no-console
    console.log("auth failure");
    const isLogin =
      error.request.responseURL && error.request.responseURL.indexOf("/login/") !== -1;
    const isPrinter = window.location.pathname.startsWith("/print/");
    const isHijack = window.location.pathname.startsWith("/hijack-token/");
    api.clearToken();
    if (!(isLogin || isPrinter || isHijack)) {
      // Refresh page only if error occured not during login, printing or token hijack.
      window.location.reload();
    }
  }
  // eslint-disable-next-line no-undef
  if (400 === error.response.status && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`BAD REQUEST: ${JSON.stringify(error.response.data)}`);
  }
  return Promise.reject(error);
};

const versionInterceptor = (response) => {
  if (!response || !response.headers) {
    return;
  }
  const apiVersion = response.headers["x-api-version"];
  if (apiVersion && window.SchoolSettings.api_version !== apiVersion) {
    window.enrolHQClientUpdateRequired = true;
    if (window.sentryConfigureScope) {
      window.sentryConfigureScope(function (scope) {
        scope.setExtra("server-version", apiVersion);
      });
    }
  }
};

axios.interceptors.request.use((request) => {
  const accessToken = tokenStorage.getAccessToken();
  if (accessToken && !request.headers.Authorization) {
    request.headers.Authorization = `Token ${accessToken}`;
  }
  return request;
});

const refreshAuthLogic = (failedRequest) => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(failedRequest);
  }
  return axios
    .post(
      "/accounts/refresh/",
      {},
      { headers: { Authorization: `Token ${refreshToken}` } },
    )
    .then((tokenRefreshResponse) => {
      tokenStorage.set(
        tokenRefreshResponse.data.access_token,
        tokenRefreshResponse.data.refresh_token,
      );
      failedRequest.response.config.headers.Authorization =
        "Token " + tokenRefreshResponse.data.access_token;
      return Promise.resolve();
    });
};

createAuthRefreshInterceptor(axios, refreshAuthLogic);

api.raw.interceptors.response.use(
  (response) => {
    versionInterceptor(response);
    return response;
  },
  (error) => {
    versionInterceptor(error.response);
    return errorInterceptor(error);
  },
);

api.init();
export default api;
